import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MarriageProfileService } from "src/rest/marriageProfileService";
import { buildShallowPatch } from "src/utils/patchUtils";
import ChangeReasonModal from "./ChangeReasonModal";
import { fetchAuthedImageObjectUrl } from "src/utils/authImage";

import type {
  MarriageProfileSectionEditModalProps,
  ProfessionalState,
  SectionState,
  SummaryState,
  PersonalState,
  FamilyState,
  LifestyleState,
  AstroState,
} from "src/types/marriageProfileSectionEdit.types";

import {
  buildBackendPatch,
  sectionTitle,
  validateSection,
} from "src/utils/marriageProfileSectionEditUtils";

import { UseAppToast } from "src/common/useAppToast";

import {
  SummaryEditForm,
  PersonalEditForm,
  ProfessionalEditForm,
  FamilyEditForm,
  LifestyleEditForm,
  AstroEditForm,
} from "./sectionForms";

export default function MarriageProfileSectionEditModal({
  isOpen,
  onClose,
  section,
  profileId,
  initialValues,
  existingSocialMediaLinks,
  existingAdditionalImages = [],
  onUpdated,
}: MarriageProfileSectionEditModalProps) {
  const { showSuccessToast, showErrorToast, showInfoToast } = UseAppToast();
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

  const [values, setValues] = useState<SectionState>(initialValues);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string>("");
  const [additionalPhotoFiles, setAdditionalPhotoFiles] = useState<(File | null)[]>([]);
  const [additionalPreviewUrls, setAdditionalPreviewUrls] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Existing server images (for the SUMMARY section) ────────────────
  const [serverImages, setServerImages] = useState<Array<{ id: number; fileName: string }>>([]);
  const [serverImageUrls, setServerImageUrls] = useState<Record<number, string>>({});
  const [deletingImageId, setDeletingImageId] = useState<number | null>(null);

  // Reason modal state
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [pendingPatch, setPendingPatch] = useState<Record<string, any> | null>(null);

  // Delete-image reason modal state
  const [pendingDeleteImageId, setPendingDeleteImageId] = useState<number | null>(null);
  const [isDeleteReasonModalOpen, setIsDeleteReasonModalOpen] = useState(false);

  const MAX_ADDITIONAL = 4;
  const MAX_PHOTO_BYTES = 1 * 1024 * 1024; // 1 MB

  // ── Reset on open ───────────────────────────────────────────────────
  React.useEffect(() => {
    if (!isOpen) return;
    setValues(initialValues);
    setErrors({});
    setPhotoFile(null);
    setPhotoPreviewUrl("");
    setAdditionalPhotoFiles([]);
    setAdditionalPreviewUrls([]);
    setServerImages(existingAdditionalImages);
    setDeletingImageId(null);
  }, [isOpen, initialValues, existingAdditionalImages]);

  // ── Fetch auth image URLs for existing server images ────────────────
  useEffect(() => {
    if (!isOpen || section !== "SUMMARY" || serverImages.length === 0) return;
    let cancelled = false;
    (async () => {
      const urlMap: Record<number, string> = {};
      for (const img of serverImages) {
        if (cancelled || !img.fileName) continue;
        try {
          const url = await fetchAuthedImageObjectUrl(img.fileName);
          if (cancelled) { URL.revokeObjectURL(url); return; }
          urlMap[img.id] = url;
        } catch { /* skip */ }
      }
      if (!cancelled) setServerImageUrls(urlMap);
    })();
    return () => { cancelled = true; };
  }, [isOpen, section, serverImages]);

  // ── Cleanup object URLs ─────────────────────────────────────────────
  useEffect(() => {
    return () => { Object.values(serverImageUrls).forEach((u) => URL.revokeObjectURL(u)); };
  }, [serverImageUrls]);

  React.useEffect(() => {
    return () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
      additionalPreviewUrls.forEach((u) => { if (u) URL.revokeObjectURL(u); });
    };
  }, [photoPreviewUrl, additionalPreviewUrls]);

  // ── Derived state ───────────────────────────────────────────────────
  const hasAnyChange = useMemo(() => {
    const patch = buildShallowPatch(initialValues as any, values as any);
    const hasNewAdditional = additionalPhotoFiles.some(Boolean);
    return Object.keys(patch).length > 0 || !!photoFile || hasNewAdditional;
  }, [initialValues, values, photoFile, additionalPhotoFiles]);

  const newFileCount = useMemo(() => additionalPhotoFiles.filter(Boolean).length, [additionalPhotoFiles]);
  const totalImageCount = serverImages.length + newFileCount;
  const availableSlots = Math.max(0, MAX_ADDITIONAL - serverImages.length);

  // ── Field helpers ───────────────────────────────────────────────────
  const setField = (key: string, val: any) => {
    setValues((prev) => ({ ...(prev as any), [key]: val }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const updateSocialLink = (idx: number, patch: Partial<{ socialMediaPlatforms: string; link: string }>) => {
    setValues((prev) => {
      const curr = (prev as any).socialMediaLinksList;
      const list: Array<{ id?: any; socialMediaPlatforms: string; link: string }> = Array.isArray(curr)
        ? curr.map((x: any) => ({
            id: x?.id,
            socialMediaPlatforms: String(x?.socialMediaPlatforms ?? x?.platform ?? ""),
            link: String(x?.link ?? x?.url ?? ""),
          }))
        : [];
      const next = list.map((x, i) => (i === idx ? { ...x, ...patch } : x));
      return { ...(prev as any), socialMediaLinksList: next };
    });
  };

  const addSocialLink = () => {
    setValues((prev) => {
      const curr = (prev as any).socialMediaLinksList;
      const list: Array<any> = Array.isArray(curr) ? curr : [];
      return { ...(prev as any), socialMediaLinksList: [...list, { socialMediaPlatforms: "", link: "" }] };
    });
  };

  const removeSocialLink = (idx: number) => {
    setValues((prev) => {
      const curr = (prev as any).socialMediaLinksList;
      const list: Array<any> = Array.isArray(curr) ? curr : [];
      return { ...(prev as any), socialMediaLinksList: list.filter((_: any, i: number) => i !== idx) };
    });
  };

  // ── Photo handler for summary ───────────────────────────────────────
  const handlePhotoChange = useCallback((file: File | null) => {
    setPhotoFile(file);
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoPreviewUrl(file ? URL.createObjectURL(file) : "");
  }, [photoPreviewUrl]);

  const handleAdditionalFileChange = useCallback((files: (File | null)[], previews: string[]) => {
    setAdditionalPhotoFiles(files);
    setAdditionalPreviewUrls(previews);
  }, []);

  // ── Delete image (reason-gated) ────────────────────────────────────
  const handleDeleteImage = useCallback((imageId: number) => {
    setPendingDeleteImageId(imageId);
    setIsDeleteReasonModalOpen(true);
  }, []);

  const handleDeleteReasonConfirm = useCallback(async (reason: string) => {
    if (pendingDeleteImageId == null) return;
    const imageId = pendingDeleteImageId;
    setIsDeleteReasonModalOpen(false);
    setDeletingImageId(imageId);
    try {
      const res = await MarriageProfileService.deleteImage(profileId, imageId, reason);
      const status = (res as any)?.status;
      if (typeof status === "number" && (status < 200 || status >= 300)) {
        throw new Error(`Delete failed (HTTP ${status})`);
      }
      setServerImages((prev) => prev.filter((img) => img.id !== imageId));
      setServerImageUrls((prev) => {
        const next = { ...prev };
        if (next[imageId]) { URL.revokeObjectURL(next[imageId]); delete next[imageId]; }
        return next;
      });
      showSuccessToast("Photo deleted");
      onUpdated?.();
    } catch (err: any) {
      showErrorToast(err?.response?.data?.message || err?.message || "Failed to delete photo");
    } finally {
      setDeletingImageId(null);
      setPendingDeleteImageId(null);
    }
  }, [pendingDeleteImageId, profileId, showSuccessToast, showErrorToast, onUpdated]);

  // ── Validate + Save ─────────────────────────────────────────────────
  const validate = () => {
    const next = validateSection(section, values);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const shallow = buildShallowPatch(initialValues as any, values as any);
    const hasAdditional = additionalPhotoFiles.some(Boolean);
    if (Object.keys(shallow).length === 0 && !photoFile && !hasAdditional) {
      showInfoToast("No changes to save");
      return;
    }

    const backendPatch = buildBackendPatch(section, shallow as any);
    const hasRealPatchContent = Object.keys(backendPatch).length > 0 || !!photoFile;

    // WORKAROUND: re-include social media links to prevent backend wipe
    if (hasRealPatchContent && section !== "PROFESSIONAL" && !backendPatch.socialMediaLinksList) {
      const existing = Array.isArray(existingSocialMediaLinks) ? existingSocialMediaLinks : [];
      if (existing.length > 0) {
        backendPatch.socialMediaLinksList = existing
          .map((x: any) => ({
            ...(x?.id != null && x.id !== "" ? { id: x.id } : {}),
            socialMediaPlatforms: String(x?.socialMediaPlatforms ?? x?.platform ?? "").trim(),
            link: String(x?.link ?? x?.url ?? "").trim(),
          }))
          .filter((x: any) => x.socialMediaPlatforms && x.link);
      }
    }

    setPendingPatch(hasRealPatchContent ? backendPatch : null);
    setIsReasonModalOpen(true);
  };

  const handleReasonConfirm = async (reason: string) => {
    try {
      setIsSaving(true);
      const hasPatchChanges = pendingPatch != null && (Object.keys(pendingPatch).length > 0 || !!photoFile);
      const stagedFiles = additionalPhotoFiles.filter(Boolean) as File[];

      if (hasPatchChanges) {
        const res = await MarriageProfileService.updatePatch(profileId, {
          data: pendingPatch,
          photo: photoFile,
          changeReason: reason,
        });
        const status = (res as any)?.status;
        if (typeof status === "number" && (status < 200 || status >= 300)) {
          throw new Error(`Update failed (HTTP ${status})`);
        }
      }

      if (stagedFiles.length > 0) {
        const uploadRes = await MarriageProfileService.uploadImages(profileId, stagedFiles, reason);
        const uploadStatus = (uploadRes as any)?.status;
        if (typeof uploadStatus === "number" && (uploadStatus < 200 || uploadStatus >= 300)) {
          throw new Error(`Image upload failed (HTTP ${uploadStatus})`);
        }
      }

      showSuccessToast("Profile updated");
      setIsReasonModalOpen(false);
      setPendingPatch(null);
      onClose();
      onUpdated?.();
    } catch (err: any) {
      showErrorToast(
        String(err?.response?.data?.message || err?.message || "Unable to update the profile.")
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Render section form ─────────────────────────────────────────────
  const renderSectionForm = () => {
    const baseProps = { errors, setField };

    switch (section) {
      case "SUMMARY":
        return (
          <SummaryEditForm
            {...baseProps}
            values={values as SummaryState}
            photoPreviewUrl={photoPreviewUrl}
            onPhotoChange={handlePhotoChange}
            serverImages={serverImages}
            serverImageUrls={serverImageUrls}
            deletingImageId={deletingImageId}
            onDeleteImage={handleDeleteImage}
            additionalPhotoFiles={additionalPhotoFiles}
            additionalPreviewUrls={additionalPreviewUrls}
            onAdditionalFileChange={handleAdditionalFileChange}
            availableSlots={availableSlots}
            totalImageCount={totalImageCount}
            maxAdditional={MAX_ADDITIONAL}
            maxPhotoBytes={MAX_PHOTO_BYTES}
            showErrorToast={showErrorToast}
          />
        );
      case "PERSONAL":
        return <PersonalEditForm {...baseProps} values={values as PersonalState} />;
      case "PROFESSIONAL":
        return (
          <ProfessionalEditForm
            {...baseProps}
            values={values as ProfessionalState}
            updateSocialLink={updateSocialLink}
            addSocialLink={addSocialLink}
            removeSocialLink={removeSocialLink}
          />
        );
      case "FAMILY":
        return <FamilyEditForm {...baseProps} values={values as FamilyState} />;
      case "LIFESTYLE":
        return <LifestyleEditForm {...baseProps} values={values as LifestyleState} />;
      case "ASTRO":
        return <AstroEditForm {...baseProps} values={values as AstroState} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "lg" }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius={{ base: 0, md: "16px" }}>
          <ModalHeader>{sectionTitle[section]}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {renderSectionForm()}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack w="full" spacing={3} justify="flex-end">
              <Button variant="outline" onClick={onClose} height={isMobile ? "48px" : undefined}>
                Cancel
              </Button>
              <Button
                colorScheme="brown"
                bg="#4A2C2A"
                _hover={{ bg: "#4A2C2A" }}
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={!hasAnyChange}
                height={isMobile ? "48px" : undefined}
              >
                Save
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reason modal – shown after Save validation passes */}
      <ChangeReasonModal
        isOpen={isReasonModalOpen}
        onClose={() => { setIsReasonModalOpen(false); setPendingPatch(null); }}
        onConfirm={handleReasonConfirm}
        isLoading={isSaving}
      />

      {/* Reason modal – shown before deleting an additional image */}
      <ChangeReasonModal
        isOpen={isDeleteReasonModalOpen}
        onClose={() => { setIsDeleteReasonModalOpen(false); setPendingDeleteImageId(null); }}
        onConfirm={handleDeleteReasonConfirm}
        isLoading={deletingImageId != null}
        title="Reason for Deleting Photo"
      />
    </>
  );
}
